import { Ban, CalendarDays, Check, CreditCard, Download, HeartHandshake, LockOpen, Users, Wand2 } from 'lucide-react';
import { MarketingLayout } from '@/components/marketing/MarketingLayout';
import { CtaLink, Eyebrow, PageHero, Reveal, SectionHeading } from '@/components/marketing/primitives';
import { BookingWorkflowMockup } from '@/components/marketing/CoreProductMockups';
import { cn } from '@/lib/utils';
import aboutPhoto from '@/assets/about-hero.jpg';
import '@/styles/marketing-pages.css';

const principles = [
  {
    icon: Wand2,
    tint: 'bg-[hsl(var(--mk-sun))]',
    title: 'Professional should be easy.',
    body: 'You shouldn’t need an agency, a developer or a nephew who “does websites” to look credible online. Pick a template, add your logo, go live.',
  },
  {
    icon: CalendarDays,
    tint: 'bg-[hsl(var(--mk-mint))]',
    title: 'The website should do the work.',
    body: 'A pretty brochure that sends you emails isn’t enough. Bookings, quotes, services, team and schedule belong in one joined-up system.',
  },
  {
    icon: Users,
    tint: 'bg-[hsl(var(--mk-lavender))]',
    title: 'Built for people on the tools.',
    body: 'Owners, supervisors and cleaners need clarity, not complicated software. Everyone sees what they need and nothing they don’t.',
  },
];

const promises = [
  { icon: Ban, title: 'No commission. Ever.', body: 'A flat monthly fee. We never take a slice of your jobs.' },
  { icon: CreditCard, title: 'Try it properly first.', body: '30 days free to build, take bookings and decide for yourself.' },
  { icon: LockOpen, title: 'No lock-in.', body: 'Cancel any time. No contract, no exit fee, no sales call.' },
  { icon: Download, title: 'Your data stays yours.', body: 'Export bookings and customers any time. We never resell it.' },
];

const workflowPoints = [
  'The real booking details and service price',
  'Confirmed instantly, or only once you approve it',
  'Assign a cleaner straight from the team view',
  'Card payment taken at booking, or an invoice linked to the job',
];

const MarketingAbout = () => (
  <MarketingLayout>
    <PageHero
      eyebrow={
        <>
          <HeartHandshake className="h-3.5 w-3.5 text-[hsl(var(--mk-sun))]" /> About us
        </>
      }
      title={
        <>
          Made for the people&nbsp;who <span className="mk-mark mk-mark-block text-[hsl(var(--mk-ink))]">do the cleaning.</span>
        </>
      }
      lede="Small cleaning companies deserve the same polish as the big franchises, without the big-franchise budget. That’s the whole idea."
    />

    {/* Origin story */}
    <section className="py-28 md:py-36">
      <div className="container mx-auto grid gap-14 px-4 lg:grid-cols-[1fr_1fr] lg:items-center lg:gap-20">
        <Reveal className="relative">
          <div className="overflow-hidden rounded-[2.5rem]">
            <img src={aboutPhoto} alt="A freshly cleaned, bright living space" className="aspect-[4/3] w-full object-cover" loading="lazy" />
          </div>
          <div className="absolute -bottom-6 left-6 right-6 flex items-center gap-4 rounded-3xl bg-white p-5 shadow-[0_24px_50px_-24px_rgba(20,16,70,0.5)] sm:left-auto sm:right-[-1.5rem] sm:max-w-xs">
            <span className="mk-icon-blob h-12 w-12 shrink-0 bg-[hsl(var(--mk-sun))]">
              <Wand2 className="h-5 w-5" />
            </span>
            <span className="leading-tight">
              <span className="block font-display font-semibold">Started as one real site</span>
              <span className="mt-1 block text-sm text-[hsl(var(--mk-muted))]">The one you can click around in our live demo.</span>
            </span>
          </div>
        </Reveal>
        <div>
          <Reveal>
            <Eyebrow>Where it started</Eyebrow>
            <h2 className="mk-h2 mt-6">
              One cleaning website that <span className="mk-mark">actually worked.</span>
            </h2>
          </Reveal>
          <Reveal delay={100} className="mk-lede mt-6 space-y-5 text-[hsl(var(--mk-muted))]">
            <p>
              The Polished Version began as the website for a single cleaning company. It sold the service, took the bookings, handled the quotes and kept the team’s week in order.
            </p>
            <p>
              So we turned it into a platform any cleaning company can use. Same polish, same booking flow, same schedule. <span className="text-[hsl(var(--mk-ink))]">Yours in 60 seconds, with your name on it.</span>
            </p>
          </Reveal>
          <Reveal delay={180} className="mt-9">
            <CtaLink to="/demo" variant="ink">
              Explore the original
            </CtaLink>
          </Reveal>
        </div>
      </div>
    </section>

    {/* Manifesto */}
    <section className="px-4">
      <div className="mk-panel container mx-auto px-6 py-20 text-center md:px-14 md:py-28">
        <Reveal>
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[hsl(var(--mk-muted))]">What we believe</p>
        </Reveal>
        <Reveal delay={80}>
          <p className="mx-auto mt-8 max-w-5xl font-display text-[clamp(1.9rem,4.2vw,3.4rem)] font-semibold leading-[1.12] tracking-[-0.035em]">
            Cleaners shouldn’t need an agency, a developer and{' '}
            <span className="mk-pill-stat">four apps</span> just to look professional and{' '}
            <span className="mk-mark">get booked.</span>
          </p>
        </Reveal>
      </div>
    </section>

    {/* Principles */}
    <section className="py-28 md:py-36">
      <div className="container mx-auto px-4">
        <SectionHeading eyebrow="How we build" title="Three rules we don’t break." />
        <div className="mt-16 grid gap-5 md:grid-cols-3">
          {principles.map((p, i) => (
            <Reveal key={p.title} delay={i * 100} className="mk-card mk-card-hover flex flex-col p-8 md:p-10">
              <div className="flex items-start justify-between">
                <span className={cn('mk-icon-blob text-[hsl(var(--mk-ink))]', p.tint)}>
                  <p.icon className="h-6 w-6" />
                </span>
                <span className="font-display text-5xl font-semibold tracking-tight text-[hsl(var(--mk-ink)/0.1)]">0{i + 1}</span>
              </div>
              <h3 className="mt-8 text-2xl leading-tight">{p.title}</h3>
              <p className="mt-3 text-[hsl(var(--mk-muted))]">{p.body}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* How the work connects (core operating flow) */}
    <section className="pb-28 md:pb-36">
      <div className="container mx-auto px-4">
        <div className="grid gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:items-center lg:gap-16">
          <div>
            <Reveal>
              <Eyebrow>How the work connects</Eyebrow>
              <h2 className="mk-h2 mt-6">
                Booked, approved, assigned, <span className="mk-mark mk-mark-mint">paid.</span>
              </h2>
              <p className="mk-lede mt-6 text-[hsl(var(--mk-muted))]">
                Confirm bookings instantly or approve each one, assign the cleaner and request payment. One flow, one screen, nothing typed out twice.
              </p>
            </Reveal>
            <ul className="mt-9 space-y-3.5">
              {workflowPoints.map((item, i) => (
                <Reveal as="li" key={item} delay={100 + i * 60} className="flex items-start gap-3 text-lg">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--mk-ink))] text-white">
                    <Check className="h-3.5 w-3.5" strokeWidth={3} />
                  </span>
                  {item}
                </Reveal>
              ))}
            </ul>
            <Reveal delay={360} className="mt-10 flex flex-wrap gap-3">
              <CtaLink to="/demo" variant="ink">
                View the live demo
              </CtaLink>
              <CtaLink to="/signup" variant="ghost" arrow={false}>
                Start free trial
              </CtaLink>
            </Reveal>
          </div>
          <Reveal delay={150} className="rounded-[2rem] bg-[hsl(var(--mk-lilac))] p-3 md:p-6">
            <BookingWorkflowMockup compact />
          </Reveal>
        </div>
      </div>
    </section>

    {/* Promises */}
    <section className="px-4 pb-28 md:pb-36">
      <div className="mk-ink-bg mk-on-dark container mx-auto overflow-hidden rounded-[2.5rem] px-6 py-20 md:px-14 md:py-24">
        <div className="grid gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:items-center">
          <Reveal>
            <Eyebrow>Our promises</Eyebrow>
            <h2 className="mk-h2 mt-6 text-white">
              Fair by default. <br className="hidden sm:block" />
              <span className="whitespace-nowrap text-[hsl(var(--mk-sun))]">No catches.</span>
            </h2>
            <p className="mk-lede mt-6 text-white/65">
              You work hard for every booking. We’re not going to skim it, trap you or hold your customer list hostage.
            </p>
          </Reveal>
          <div className="grid gap-4 sm:grid-cols-2">
            {promises.map((p, i) => (
              <Reveal key={p.title} delay={i * 80} className="rounded-[1.75rem] border border-white/10 bg-white/[0.05] p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[hsl(var(--mk-mint))] text-[hsl(var(--mk-ink))]">
                  <p.icon className="h-5 w-5" />
                </span>
                <h3 className="mt-5 text-xl text-white">{p.title}</h3>
                <p className="mt-2 text-white/60">{p.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  </MarketingLayout>
);

export default MarketingAbout;
